import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, CalendarDays, FilterX, GripVertical, ListIcon, Plus } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DateRangeFilter, endOfDayParam } from '@/components/ui/date-range-filter';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { MemberSelect } from '@/features/members';
import { CRM_OWNER_ROLES } from '@/features/crm/constants';
import { resolveTagColor, tagChipStyle } from '@/features/tags/colors';
import { useSession } from '@/stores/session';
import { canSeeMoney, formatMoney, formatMoneyCompact } from '@/lib/money';
import { cn } from '@/lib/utils';
import { DEAL_STAGES, type DealStage } from '../constants';
import { LostReasonDialog } from '../components/LostReasonDialog';
import { useDeals, useSetDealStage } from '../queries';
import { useDealsUi } from '../store/viewPrefs';
import type { Deal } from '../types';

const STAGE_BG: Record<DealStage, string> = {
  qualified: 'border-t-slate-400',
  proposal: 'border-t-blue-500',
  negotiation: 'border-t-amber-500',
  won: 'border-t-emerald-600',
  lost: 'border-t-rose-600',
};

interface CardProps {
  deal: Deal;
  canSeeAmount: boolean;
  onDragStart: (id: number) => void;
}

function DealCard({ deal, canSeeAmount, onDragStart }: CardProps) {
  const money =
    canSeeAmount && deal.amount && deal.currency
      ? formatMoney(deal.amount, deal.currency)
      : null;
  return (
    <Link
      to={`/crm/deals/${deal.public_id}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(deal.id));
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(deal.id);
      }}
      className="bg-card hover:border-primary block cursor-grab rounded-md border p-3 shadow-sm transition-colors active:cursor-grabbing"
      aria-label={`Deal ${deal.name}`}
    >
      <div className="flex items-start gap-2">
        <GripVertical className="text-muted-foreground mt-0.5 size-4 shrink-0" />
        <div className="min-w-0 flex-1 space-y-2">
          <p className="line-clamp-2 text-sm font-medium">{deal.name}</p>

          {deal.tags.length ? (
            <ul className="flex flex-wrap gap-1">
              {deal.tags.map((t) => (
                <li key={t.id}>
                  <Badge
                    variant="outline"
                    className="px-1.5 py-0 text-[10px]"
                    style={tagChipStyle(resolveTagColor(t))}
                  >
                    {t.name}
                  </Badge>
                </li>
              ))}
            </ul>
          ) : null}

          {/* Value is the headline figure; win-probability rides alongside as a pill. */}
          <div className="flex items-center justify-between gap-2">
            {money ? (
              <span className="text-foreground text-sm font-semibold tabular-nums">{money}</span>
            ) : canSeeAmount ? (
              <span className="text-muted-foreground text-sm">No estimate</span>
            ) : (
              <Badge variant="outline" className="text-[10px]">
                Restricted
              </Badge>
            )}
            <span
              className="bg-muted text-muted-foreground shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums"
              title="Win probability"
            >
              {Number(deal.probability)}%
            </span>
          </div>

          <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
            <span className="inline-flex items-center gap-1" title="Created">
              <CalendarDays className="size-3 shrink-0" />
              {new Date(deal.created).toLocaleDateString()}
            </span>
            {deal.expected_close_date ? (
              <span className="inline-flex items-center gap-1" title="Expected close">
                <CalendarClock className="size-3 shrink-0" />
                {new Date(deal.expected_close_date).toLocaleDateString()}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </Link>
  );
}

interface ColumnProps {
  stage: DealStage;
  label: string;
  deals: Deal[];
  canSeeAmount: boolean;
  onDrop: (dealId: number, target: DealStage) => void;
  onDragStart: (id: number) => void;
  isDropTarget: boolean;
  setDropTarget: (stage: DealStage | null) => void;
}

function Column({
  stage,
  label,
  deals,
  canSeeAmount,
  onDrop,
  onDragStart,
  isDropTarget,
  setDropTarget,
}: ColumnProps) {
  // Money totals (currency-aware) — skip when the role can't see money at all.
  const totals = useMemo(() => {
    if (!canSeeAmount) return [];
    const sums = new Map<string, number>();
    for (const d of deals) {
      if (!d.amount || !d.currency) continue;
      sums.set(d.currency, (sums.get(d.currency) ?? 0) + Number(d.amount));
    }
    return [...sums.entries()];
  }, [deals, canSeeAmount]);

  return (
    <div
      className="flex w-72 shrink-0 flex-col"
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (!isDropTarget) setDropTarget(stage);
      }}
      onDragLeave={(e) => {
        // Only clear when we truly leave the column (not on a child enter).
        if (e.currentTarget === e.target) setDropTarget(null);
      }}
      onDrop={(e) => {
        e.preventDefault();
        const idStr = e.dataTransfer.getData('text/plain');
        const id = Number(idStr);
        if (Number.isFinite(id)) onDrop(id, stage);
        setDropTarget(null);
      }}
    >
      <div className={cn('rounded-t-md border-t-4 bg-muted/40 p-3', STAGE_BG[stage])}>
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">{label}</h2>
          <div className="flex items-center gap-1.5">
            {totals.length ? (
              <span className="text-foreground text-xs font-semibold tabular-nums">
                {totals
                  .map(([c, t]) => formatMoneyCompact(String(t), c))
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            ) : null}
            <Badge variant="outline" className="tabular-nums">
              {deals.length}
            </Badge>
          </div>
        </div>
      </div>
      <div
        className={cn(
          'bg-muted/20 flex min-h-[200px] flex-1 flex-col gap-2 rounded-b-md p-2 transition-colors',
          isDropTarget && 'bg-primary/10 ring-primary/30 ring-2 ring-inset',
        )}
      >
        {deals.length === 0 ? (
          <p className="text-muted-foreground py-6 text-center text-xs">No deals</p>
        ) : (
          deals.map((d) => (
            <DealCard key={d.id} deal={d} canSeeAmount={canSeeAmount} onDragStart={onDragStart} />
          ))
        )}
      </div>
    </div>
  );
}

export function DealsBoardPage() {
  const role = useSession((s) => s.role);
  const canSeeAmount = canSeeMoney(role);

  // Board filters persist across reloads via the deals UI store. Created is
  // always set, so the quarter/month quick-pick actually narrows the board
  // (expected_close_date is frequently blank).
  const owner = useDealsUi((s) => s.boardOwner);
  const setOwner = useDealsUi((s) => s.setBoardOwner);
  const createdFrom = useDealsUi((s) => s.boardCreatedFrom);
  const setCreatedFrom = useDealsUi((s) => s.setBoardCreatedFrom);
  const createdTo = useDealsUi((s) => s.boardCreatedTo);
  const setCreatedTo = useDealsUi((s) => s.setBoardCreatedTo);
  const { data, isPending, isError, error } = useDeals({
    owner: owner ? Number(owner) : undefined,
    created_after: createdFrom || undefined,
    created_before: endOfDayParam(createdTo),
  });
  const setStage = useSetDealStage();
  const hasFilters = owner !== '' || createdFrom !== '' || createdTo !== '';

  function clearFilters() {
    setOwner('');
    setCreatedFrom('');
    setCreatedTo('');
  }

  const [draggingId, setDraggingId] = useState<number | null>(null);
  // A drop on Lost waits here until the reason is given (ADR-108).
  const [losing, setLosing] = useState<Deal | null>(null);
  const [dropTarget, setDropTarget] = useState<DealStage | null>(null);

  // Bucket deals by stage, preserving the API order within each column.
  const buckets = useMemo(() => {
    const map = new Map<DealStage, Deal[]>();
    for (const s of DEAL_STAGES) map.set(s.value, []);
    for (const d of data?.results ?? []) {
      const bucket = map.get(d.stage as DealStage);
      if (bucket) bucket.push(d);
    }
    return map;
  }, [data]);

  function handleDrop(dealId: number, target: DealStage) {
    const current = data?.results.find((d) => d.id === dealId);
    if (!current || current.stage === target) return;
    setDraggingId(null);
    if (target === 'lost') {
      setLosing(current);
      return;
    }
    setStage.mutate({ id: dealId, stage: target });
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Pipeline</h1>
          <p className="text-muted-foreground text-sm">
            Drag deals across stages — changes are saved and audited.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline">
            <Link to="/crm/deals">
              <ListIcon className="size-4" /> Table view
            </Link>
          </Button>
          <Button asChild>
            <Link to="/crm/deals/new">
              <Plus className="size-4" /> New deal
            </Link>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="w-60">
          <MemberSelect
            value={owner}
            onChange={setOwner}
            placeholder="All sales"
            queryKey="board-owner-filter"
            roles={CRM_OWNER_ROLES}
            clearable
          />
        </div>
        <DateRangeFilter
          label="Created"
          idPrefix="board-created"
          from={createdFrom}
          to={createdTo}
          onFromChange={setCreatedFrom}
          onToChange={setCreatedTo}
        />
        {hasFilters ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={clearFilters}
            aria-label="Reset all filters"
            title="Reset all filters"
          >
            <FilterX className="size-4" />
          </Button>
        ) : null}
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load deals.'}
        </p>
      ) : isPending ? (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {DEAL_STAGES.map((s) => (
            <Skeleton key={s.value} className="h-72 w-72 shrink-0 rounded-md" />
          ))}
        </div>
      ) : (
        <div
          className="flex gap-4 overflow-x-auto pb-2"
          aria-live="polite"
          // Clear the dragging id if the user drops outside any column.
          onDragEnd={() => {
            setDraggingId(null);
            setDropTarget(null);
          }}
        >
          {DEAL_STAGES.map((s) => (
            <Column
              key={s.value}
              stage={s.value}
              label={s.label}
              deals={buckets.get(s.value) ?? []}
              canSeeAmount={canSeeAmount}
              onDrop={handleDrop}
              onDragStart={setDraggingId}
              isDropTarget={dropTarget === s.value && draggingId != null}
              setDropTarget={setDropTarget}
            />
          ))}
        </div>
      )}

      <LostReasonDialog
        dealName={losing?.name ?? null}
        onCancel={() => setLosing(null)}
        onConfirm={(lost) => {
          if (losing) setStage.mutate({ id: losing.id, stage: 'lost', lost });
          setLosing(null);
        }}
      />
    </div>
  );
}
